import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography
} from '@mui/material';
import ReactCrop, { centerCrop, makeAspectCrop,convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const ReactCropModal = ({
  open,
  onClose,
  currentImage,
  onCropComplete,
  aspectRatio,
  onClearFile,
  /** Use PNG to preserve transparency (logos). Otherwise JPEG. */
  preferPng = false
}) => {
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);

  // Canvas defaults uninitialized/exported pixels to opaque black, and JPEG has
  // no alpha channel at all, so any transparent source image gets a black
  // background baked in unless we detect real transparency and switch to PNG.
  const canvasHasTransparency = (ctx, width, height) => {
    try {
      const { data } = ctx.getImageData(0, 0, width, height);
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 255) return true;
      }
      return false;
    } catch (e) {
      // Tainted canvas (cross-origin image without permissive CORS) - can't inspect pixels.
      return false;
    }
  };

  const getCroppedImg = (image, crop, fileType = "image/jpeg", cropAspectRatio = null) => {
  return new Promise((resolve, reject) => {
    if (!crop?.width || !crop?.height) {
      reject("Invalid crop");
      return;
    }

    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    // Calculate crop dimensions in original image pixels
    const cropWidth = crop.width * scaleX;
    const cropHeight = crop.height * scaleY;
    
    // For 5:2 aspect ratio (cover_image), always use recommended dimensions 1752x701
    let outputWidth = cropWidth;
    let outputHeight = cropHeight;
    
    // Force specific dimensions based on aspect ratio
    if (cropAspectRatio !== null && cropAspectRatio !== undefined) {
      if (Math.abs(cropAspectRatio - 2.5) < 0.01) {
        // For 5:2 aspect ratio (2.5), use recommended dimensions 1750x700 (exact 5:2)
        outputWidth = 1750;
        outputHeight = 700;
      } else if (Math.abs(cropAspectRatio - 4.0) < 0.01) {
        // For 4:1 aspect ratio (4.0), use recommended dimensions 2000x500 (exact 4:1)
        outputWidth = 2000;
        outputHeight = 500;
      } else if (Math.abs(cropAspectRatio - 1.5) < 0.01) {
        // For 3:2 aspect ratio (1.5), use recommended dimensions
        outputWidth = 328;
        outputHeight = 220;
      } else if (Math.abs(cropAspectRatio - 1.0) < 0.01) {
        // For 1:1 aspect ratio (profile image), use recommended dimensions 500x500
        outputWidth = 500;
        outputHeight = 500;
      }
    }
    
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const ctx = canvas.getContext("2d");

    // ✅ preserve transparency
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw the cropped image, scaling it to fit the output dimensions
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      cropWidth,
      cropHeight,
      0,
      0,
      outputWidth,
      outputHeight
    );

    // If the crop actually contains transparent pixels, force PNG regardless
    // of the requested fileType so the transparency survives export.
    const outputType =
      fileType !== "image/png" && canvasHasTransparency(ctx, outputWidth, outputHeight)
        ? "image/png"
        : fileType;
    const quality = outputType === "image/png" ? 1 : 0.95;
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject("Canvas is empty");
          return;
        }
        resolve(blob);
      },
      outputType,
      quality
    );
  });
};




 const handleCropComplete = async () => {
  if (!imgRef.current) {
    return;
  }

  // Use completedCrop if available, otherwise fall back to crop
  const cropToUse = completedCrop?.width && completedCrop?.height ? completedCrop : crop;
  
  if (!cropToUse?.width || !cropToUse?.height) {
    return;
  }

  // ✅ Convert % crop to pixel crop
  const pixelCrop = convertToPixelCrop(
    cropToUse,
    imgRef.current.width,
    imgRef.current.height
  );

  try {
    const fileType = preferPng ? "image/png" : "image/jpeg";
    const blob = await getCroppedImg(imgRef.current, pixelCrop, fileType, aspectRatio);
    onCropComplete(blob); // Pass the real cropped blob
    // Clear file input
    if (onClearFile) {
      onClearFile();
    }
    onClose();
  } catch (err) {
    console.error("Crop failed:", err);
  }
};




  const handleClose = () => {
    setCrop(null);
    setCompletedCrop(null);
    // Clear file input
    if (onClearFile) {
      onClearFile();
    }
    onClose();
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;

    if (aspectRatio) {
      const crop = centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: 90, // default width
          },
          aspectRatio,
          width,
          height
        ),
        width,
        height
      );
      setCrop(crop);
      // Set completedCrop to the initial crop so Apply button works without user interaction
      setCompletedCrop(crop);
    } else {
      const defaultCrop = {
        unit: '%',
        width: 100,
        height: 100,
        x: 0,
        y: 0,
      };
      setCrop(defaultCrop);
      // Set completedCrop to the initial crop so Apply button works without user interaction
      setCompletedCrop(defaultCrop);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogContent>
        <Typography variant="h5" gutterBottom>
          Crop Image
        </Typography>
        <Box display="flex" flexDirection="column" alignItems="center">
          {currentImage && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspectRatio || undefined}
            >
              <img
                ref={imgRef}
                crossOrigin="anonymous"
                alt="Crop me"
                src={currentImage}
                style={{ maxWidth: '100%' }}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleCropComplete}
          variant="contained"
          color="primary"
          disabled={!completedCrop?.width}
        >
          Apply Crop
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReactCropModal;
